import React, { useEffect, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import CalenderIcon from '@svg/calendar.svg?react';
import moment from 'moment';
import {
    DATE_OPTIONS_MAP,
    DateOptionKey,
    DateRange,
    DateRangeObject, subtractDaysFromToday,
    WORDS_MONTHS_FORMAT
} from '../../../utils/datesUtils';
import 'react-day-picker/dist/style.css';
import './date-range-picker.scss';
import CustomButton from '../custom-button/custom-button';
import { Popover } from 'antd';
import { keyboardClickable } from '../../../utils/a11y-utils';

export interface DateOption {
    value: number;
    unit: any;
}

export interface DateRangePickerProps {
    defaultValue: DateRange;
    onDateRangeChange: (selectedOption: DateOptionKey, range: DateRangeObject | undefined) => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({ defaultValue, onDateRangeChange }) => {
    const [selectedOption, setSelectedOption] = useState<string>();
    const [range, setRange] = useState<any>();
    const [isOpen, setIsOpen] = useState(false);

    const until = new Date();
    const fromDate = subtractDaysFromToday(window.CONFIG.REPORTS_MAX_DAYS_RANGE - 1);

    useEffect(() => {
        if (typeof defaultValue === 'string') {
            setSelectedOption(defaultValue);
            setRange(undefined);
        } else {
            setRange(defaultValue);
            setSelectedOption(getSelectionText(defaultValue));
        }
    }, [defaultValue]);

    const handleMenuClick = (option: DateOptionKey) => {
        setSelectedOption(option);
        setRange(undefined);
        onDateRangeChange(option, undefined);
        setIsOpen(false);
    };

    const onApplyDateRange = () => {
        const newSelectedOption = getSelectionText(range);
        setSelectedOption(newSelectedOption);
        onDateRangeChange(newSelectedOption, range);
        setIsOpen(false);
    };

    const getSelectionText = (range: DateRangeObject): string => {
        if (range.from && range.to) {
            const from = moment(range.from).format(WORDS_MONTHS_FORMAT);
            const to = moment(range.to).format(WORDS_MONTHS_FORMAT);
            return `${from} - ${to}`;
        }
        return `${selectedOption}`;
    };

    return <Popover trigger="click" showArrow={false}
                open={isOpen}
                onOpenChange={setIsOpen}
                placement={'bottomLeft'} overlayClassName="date-range-picker-popup"
                content={
                     <div className="content-wrapper">
                         <div className="close-options">
                             {Object.keys(DATE_OPTIONS_MAP).map((option) => (
                                 <div className={`date-option ${selectedOption === option ? 'active' : ''}`}
                                      key={option}
                                      {...keyboardClickable(() => handleMenuClick(option))}>
                                     {option}
                                 </div>
                             ))}
                         </div>
                         <div className="date-range-picker">
                             <DayPicker
                                 mode="range"
                                 defaultMonth={until}
                                 selected={range}
                                 onSelect={setRange}
                                 disabled={{before: fromDate, after: until}}
                                 max={40}
                                 fromMonth={fromDate}
                                 toMonth={until}
                             />
                             <CustomButton label={'Apply Date Range'} className="range-apply-btn primary-btn"
                                           onClick={onApplyDateRange}
                                           isDisabled={!range?.from || !range?.to}/>
                         </div>
                     </div>}
        >
            <div className="date-range-picker-selection-date">
                <CalenderIcon/>
                <span>{selectedOption}</span>
            </div>
        </Popover>;
};

export default DateRangePicker;
